import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  SxProps,
  Theme
} from '@mui/material';

export interface DosenDropdownOption {
  value: string;
  label: string;
}

export interface DosenDropdownProps {
  options: DosenDropdownOption[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  hideIcon?: boolean;
  sx?: SxProps<Theme>;
  menuSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
}

const BASE_WIDTH = 288;

// MUI's <Select> injects its own positioning/rotate-on-open classes (e.g.
// .MuiSelect-icon, .MuiSelect-iconOpen) onto IconComponent via `className` —
// forward it (and any other props) so the icon is actually positioned and
// flips open/closed instead of rendering inline and unstyled.
const ArrowIcon = ({ className, ...rest }: { className?: string }) => (
  <Box
    className={className}
    {...rest}
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        width: 12,
        height: 6,
        borderBottom: '2px solid var(--Stroke-icon-dim, rgba(134,134,134,0.8))',
        borderRight: '2px solid var(--Stroke-icon-dim, rgba(134,134,134,0.8))',
        transform: 'rotate(45deg)'
      }}
    />
  </Box>
);

const NoIcon = () => null;

export default function DosenDropdown({
  options,
  value,
  onChange,
  placeholder = 'Please Select',
  label,
  disabled = false,
  multiple = false,
  hideIcon = false,
  sx,
  menuSx,
  inputSx
}: DosenDropdownProps) {
  const handleChange = (e: SelectChangeEvent<string | string[]>) => {
    const rawValue = e.target.value as string | string[];
    if (multiple) {
      const nextValue = Array.isArray(rawValue) ? rawValue : [rawValue];
      onChange?.(nextValue);
      return;
    }
    onChange?.(rawValue as string);
  };

  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : value
      ? [value]
      : []
    : value ?? '';
  const selectedValues = multiple
    ? Array.isArray(normalizedValue)
      ? normalizedValue
      : []
    : Array.isArray(value)
    ? value
    : value
    ? [value]
    : [];
  const isEmpty = multiple ? selectedValues.length === 0 : !value;

  const handleDelete = (deletedValue: string) => {
    if (!multiple) return;
    const nextValue = selectedValues.filter((val) => val !== deletedValue);
    onChange?.(nextValue);
  };

  return (
    <FormControl
      disabled={disabled}
      sx={{
        width: BASE_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        opacity: disabled ? 0.5 : 1,
        ...sx
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FFF)',
            lineHeight: '20px'
          }}
        >
          {label}
        </Typography>
      )}

      <Select<string | string[]>
        value={normalizedValue}
        onChange={handleChange}
        displayEmpty
        disabled={disabled}
        multiple={multiple}
        IconComponent={(hideIcon ? NoIcon : ArrowIcon) as any}
        MenuProps={{
          PaperProps: {
            sx: {
              width: BASE_WIDTH,
              mt: 1,
              borderRadius: 1,
              background: 'rgba(255,255,255,0.07)',
              outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
              boxShadow: 'none',
              overflow: 'hidden',
              ...menuSx
            }
          }
        }}
        sx={{
          // base container
          pl: 3,
          pr: hideIcon ? 3 : '38px',
          py: 2,
          background: 'rgba(255,255,255,0.07)',
          borderRadius: 1,
          outline: '1px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
          display: 'flex',
          alignItems: 'center',

          '& .MuiSelect-select': {
            p: 0,
            // reserve room for the arrow icon so long values ellipsize before
            // running underneath it instead of rendering hidden behind it
            pr: hideIcon ? '0px !important' : '28px !important',
            fontSize: 14,
            fontFamily: 'Inter',
            lineHeight: '20px',
            color: isEmpty
              ? 'var(--Text-text-tertiary, rgba(255,255,255,0.55))'
              : 'var(--Text-text-primary, #FFF)',
            display: 'flex',
            alignItems: 'center'
          },

          // pin the icon at a fixed inset instead of relying on MUI's default
          // (which sits right at the edge and gets clipped by the container)
          '& .MuiSelect-icon': hideIcon
            ? { display: 'none' }
            : {
                right: '10px',
                top: 'calc(50% - 12px)'
              },

          // remove default outline
          '& fieldset': { display: 'none' },

          // OPEN state outline highlight
          '&.Mui-focused': {
            outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
          },

          ...inputSx
        }}
        renderValue={(selected) => {
          if (!selected || (Array.isArray(selected) && selected.length === 0))
            return (
              <Typography
                sx={{
                  color: 'var(--Text-text-tertiary, rgba(255,255,255,0.55))',
                  fontSize: 14,
                  fontFamily: 'Inter',
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {placeholder}
              </Typography>
            );

          if (multiple && Array.isArray(selected)) {
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selected.map((selectedValue) => {
                  const option = options.find((o) => o.value === selectedValue);
                  return (
                    <Chip
                      key={selectedValue}
                      label={option?.label ?? selectedValue}
                      onMouseDown={(event) => event.stopPropagation()}
                      onDelete={() => handleDelete(selectedValue)}
                      sx={{
                        height: 22,
                        borderRadius: '20px',
                        background: 'var(--Fill-Button-button-white, #FDFDFD)',
                        color: 'var(--Stroke-solid-black, #0F0F0F)',
                        fontSize: 12,
                        fontFamily: 'Inter',
                        '& .MuiChip-label': { px: 1 },
                        '& .MuiChip-deleteIcon': { color: 'var(--Stroke-solid-black, #0F0F0F)' }
                      }}
                    />
                  );
                })}
              </Box>
            );
          }

          const option = options.find((o) => o.value === selected);
          return (
            <Typography
              sx={{
                color: 'var(--Text-text-primary, #FFF)',
                fontSize: 14,
                fontFamily: 'Inter',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {option?.label}
            </Typography>
          );
        }}
      >
        {options.map((opt) => {
          const isSelected = multiple ? selectedValues.includes(opt.value) : opt.value === value;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                px: 3,
                py: 1,
                fontSize: 14,
                fontFamily: 'Inter',
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: multiple ? 1.5 : 0,

                color: 'var(--Text-text-primary, #FFF)',

                background: isSelected
                  ? 'rgba(255,255,255,0.12)'
                  : 'transparent',

                fontWeight: isSelected ? 700 : 400,

                '&:hover': {
                  background: 'rgba(255,255,255,0.12)'
                }
              }}
            >
              {multiple && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '3px',
                    background: 'rgba(255,255,255,0.9)',
                    outline: isSelected
                      ? '2px solid var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.7))'
                      : '2px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.5))',
                    outlineOffset: '-1px'
                  }}
                />
              )}
              {opt.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
